// context/Web3Context.js
import { createContext, useContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';

const Web3Context = createContext();


const TOKEN_ADDRESS = "0x62CDe9Aa2159461F094e3eaCBa10F7C45C0A6B4f";

// MINE/ETH LP Token address (Uniswap V2 Pair)
const LP_TOKEN_ADDRESS = "0x503FD866B57315C643EB14080e45B5B85ff9C183"; 
const WETH_ADDRESS = "0xF298158CAdb14B8B549c8135ebFDCa629A59526a";

// ERC20 ABI (for token balances)
const ERC20_ABI = [
  "function balanceOf(address account) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
];

// Uniswap V2 Pair ABI (includes getReserves)
const UNISWAP_V2_PAIR_ABI = [
  "function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
  "function token0() external view returns (address)",
  "function token1() external view returns (address)",
  "function totalSupply() external view returns (uint256)",
  "function balanceOf(address owner) external view returns (uint256)",
  "function decimals() external view returns (uint8)",
];

const TOKEN_ABI = [
  "function mint() external",
  "function balanceOf(address account) view returns (uint256)",
  "function canMint(address account) view returns (bool)",
  "function getMintAmount(address account) view returns (uint256)",
  "function getNextMintTime(address account) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function maxSupplyNow() view returns (uint256)"
];

export function Web3Provider({ children }) {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [contract, setContract] = useState(null);
  const [ethBalance, setEthBalance] = useState("0");
  const [tokenBalance, setTokenBalance] = useState("0");
  const [canMint, setCanMint] = useState(false);
  const [mintAmount, setMintAmount] = useState("0");
  const [nextMintTime, setNextMintTime] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [totalSupply, setTotalSupply] = useState("0");
  const [isConnecting, setIsConnecting] = useState(false);
  
  // New state variables for LP data
  const [lpTokenBalance, setLpTokenBalance] = useState("0");
  const [mineReserve, setMineReserve] = useState("0");
  const [ethReserve, setEthReserve] = useState("0");
  const [minePriceInETH, setMinePriceInETH] = useState("0");
  const [lpTotalSupply, setLpTotalSupply] = useState("0");
  const [lpDecimals, setLpDecimals] = useState(18);
  const [tokenDecimals, setTokenDecimals] = useState(18);

  const [maxSupplyNow, setMaxSupplyNow] = useState("0");

  const formatCountdown = (seconds) => {
    if (seconds <= 0) return "Now";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  // Countdown timer
  useEffect(() => {
    if (!nextMintTime) return;
    const interval = setInterval(() => {
      const diff = Math.floor(nextMintTime - Date.now() / 1000);
      setCountdown(formatCountdown(diff));
    }, 1000);
    return () => clearInterval(interval);
  }, [nextMintTime]);

  // Check if MetaMask is installed
  const isMetaMaskInstalled = () => {
    return typeof window.ethereum !== 'undefined';
  };

  // Fetch LP token data and calculate price
  const fetchLPData = async () => {
    if (!provider || !account) return;

    try {
      // Create contract instances
      const lpContract = new ethers.Contract(LP_TOKEN_ADDRESS, UNISWAP_V2_PAIR_ABI, provider);
      const wethContract = new ethers.Contract(WETH_ADDRESS, ERC20_ABI, provider);
      const mineContract = new ethers.Contract(TOKEN_ADDRESS, ERC20_ABI, provider);

      // Fetch all data in parallel
      const [
        lpBalanceRaw,
        lpTotalSupplyRaw,
        reserves,
        token0Address,
        lpDecimalsVal,
        tokenDecimalsVal,
        wethBalanceInLP,
        mineBalanceInLP
      ] = await Promise.all([
        lpContract.balanceOf(account),
        lpContract.totalSupply(),
        lpContract.getReserves(),
        lpContract.token0(),
        lpContract.decimals(),
        mineContract.decimals(),
        wethContract.balanceOf(LP_TOKEN_ADDRESS),
        mineContract.balanceOf(LP_TOKEN_ADDRESS)
      ]);

      // Set LP token balance
      const lpBalance = ethers.formatUnits(lpBalanceRaw, lpDecimalsVal);
      setLpTokenBalance(lpBalance);
      setLpDecimals(lpDecimalsVal);
      setLpTotalSupply(ethers.formatUnits(lpTotalSupplyRaw, lpDecimalsVal));
      setTokenDecimals(tokenDecimalsVal);


      // Determine which reserve is MINE and which is WETH
      let mineReserveRaw, ethReserveRaw;
      if (token0Address.toLowerCase() === TOKEN_ADDRESS.toLowerCase()) {
        // token0 is MINE, token1 is WETH
        mineReserveRaw = reserves.reserve0;
        ethReserveRaw = reserves.reserve1;
      } else {
        // token1 is MINE, token0 is WETH
        mineReserveRaw = reserves.reserve1;
        ethReserveRaw = reserves.reserve0;
      }

      // Convert reserves to readable units
      const mineReserveFormatted = ethers.formatUnits(mineReserveRaw, tokenDecimalsVal);
      const ethReserveFormatted = ethers.formatUnits(ethReserveRaw, 18); // WETH has 18 decimals

      setMineReserve(mineReserveFormatted);
      setEthReserve(ethReserveFormatted);

      // Calculate MINE price in ETH
      if (parseFloat(mineReserveFormatted) > 0) {
        const price = parseFloat(ethReserveFormatted) / parseFloat(mineReserveFormatted);
        setMinePriceInETH(price.toString());
      } else {
        setMinePriceInETH("0");
      }

    } catch (err) {
      console.error("Error fetching LP data:", err);
      // Fallback: use direct balance queries
      try {
        const wethContract = new ethers.Contract(WETH_ADDRESS, ERC20_ABI, provider);
        const mineContract = new ethers.Contract(TOKEN_ADDRESS, ERC20_ABI, provider);
        
        const [wethBalanceRaw, mineBalanceRaw] = await Promise.all([
          wethContract.balanceOf(LP_TOKEN_ADDRESS),
          mineContract.balanceOf(LP_TOKEN_ADDRESS)
        ]);

        const wethBalance = ethers.formatUnits(wethBalanceRaw, 18);
        const mineBalance = ethers.formatUnits(mineBalanceRaw, await mineContract.decimals());
        
        setEthReserve(wethBalance);
        setMineReserve(mineBalance);
        
        if (parseFloat(mineBalance) > 0) {
          const price = parseFloat(wethBalance) / parseFloat(mineBalance);
          setMinePriceInETH(price.toString());
        }
      } catch (fallbackErr) {
        console.error("Fallback LP data fetch failed:", fallbackErr);
      }
    }
  };

  // Connect wallet
  const connectWallet = async () => {
    if (!isMetaMaskInstalled()) {
      alert("Please install MetaMask!");
      return;
    }

    setIsConnecting(true);
    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });
      
      const prov = new ethers.BrowserProvider(window.ethereum);
      setProvider(prov);
      const signer = await prov.getSigner();
      setContract(new ethers.Contract(TOKEN_ADDRESS, TOKEN_ABI, signer));
      setAccount(accounts[0]);
      
      // Load all data
      await loadData();
      await fetchLPData();
      
    } catch (err) {
      console.error("Connection error:", err);
      alert("Failed to connect: " + err.message);
    } finally {
      setIsConnecting(false);
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    setAccount(null);
    setProvider(null);
    setContract(null);
    setEthBalance("0");
    setTokenBalance("0");
    setCanMint(false);
    setMintAmount("0");
    setNextMintTime(null);
    setCountdown("");
    setTotalSupply("0");
    // Reset LP data
    setLpTokenBalance("0");
    setMineReserve("0");
    setEthReserve("0");
    setMinePriceInETH("0");
    setLpTotalSupply("0");
  };

  // Load data from contract
  const loadData = async () => {
    if (!contract || !account) return;
    
    try {
      const [ethBal, tokenBalRaw, canMintNow, mintAmtRaw, nextMintRaw, decimals, totalSupplyRaw, maxSupplyNowRaw] =
        await Promise.all([
          provider.getBalance(account),
          contract.balanceOf(account),
          contract.canMint(account),
          contract.getMintAmount(account),
          contract.getNextMintTime(account),
          contract.decimals(),
          contract.totalSupply(),
          contract.maxSupplyNow()
        ]);

      setEthBalance(ethers.formatEther(ethBal));
      setTokenBalance(ethers.formatUnits(tokenBalRaw, decimals));
      setCanMint(canMintNow);
      setMintAmount(ethers.formatUnits(mintAmtRaw, decimals));
      setNextMintTime(Number(nextMintRaw));
      setTotalSupply(ethers.formatUnits(totalSupplyRaw, decimals));
      setMaxSupplyNow(ethers.formatUnits(maxSupplyNowRaw, decimals));
    } catch (err) {
      console.error("Load data error:", err);
    }
  };

  // Combined data refresh
  const refreshAllData = async () => {
    if (contract && account && provider) {
      await loadData();
      await fetchLPData();
    }
  };

  // Handle mint
  const handleMint = async () => {
    if (!contract) return;
    try {
      const tx = await contract.mint();
      await tx.wait();
      await refreshAllData();
      alert("Mint successful!");
    } catch (err) {
      console.error(err);
      alert("Mint failed: " + (err.reason || err.message));
    }
  };

  // Calculate LP value in USD (if you add ETH price later)
  const calculateLPValue = () => {
    const lpShare = parseFloat(lpTokenBalance) / parseFloat(lpTotalSupply);
    const ethValue = lpShare * parseFloat(ethReserve) * 2; // *2 because LP tokens represent both sides
    return ethValue;
  };

  // Auto-load data when contract/account changes
  useEffect(() => {
    if (contract && account && provider) {
      refreshAllData();
      const interval = setInterval(() => refreshAllData(), 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [contract, account, provider]);

  // Listen for account changes
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = async (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else {
        setAccount(accounts[0]);
        if (provider) {
          await refreshAllData();
        }
      }
    };

    const handleChainChanged = () => {
      window.location.reload();
    };

    // Add event listeners
    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    // Cleanup function
    return () => {
      if (window.ethereum) {
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
        } else if (window.ethereum.off) {
          window.ethereum.off('accountsChanged', handleAccountsChanged);
          window.ethereum.off('chainChanged', handleChainChanged);
        }
      }
    };
  }, [provider]);

  const value = {
    account,
    provider,
    contract,
    ethBalance,
    tokenBalance,
    canMint,
    mintAmount,
    nextMintTime,
    countdown,
    totalSupply,
    maxSupplyNow,
    isConnecting,
    // LP data
    lpTokenBalance,
    mineReserve,
    ethReserve,
    minePriceInETH,
    lpTotalSupply,
    calculateLPValue,
    // Functions
    connectWallet,
    disconnectWallet,
    handleMint,
    refreshAllData,
    isMetaMaskInstalled
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
}

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};